import axios from 'axios';
import { AUTH_BASE_URL } from '../config/serverApiConfig';
import { authHeaders } from './mfa.service';
import { ROLE_TIER_RANK, roleTierRank } from '../config/capabilities';

/**
 * Administering somebody else's account: switching it off, switching it back on, and
 * clearing a second factor they can no longer produce.
 *
 * Like `mfa.service`, these use raw `axios` and let the error through rather than going via
 * `errorHandler`: every refusal here is a 403 whose `detail` says *which* rule refused, and
 * the screens put that wording next to the control instead of raising a generic toast.
 *
 * The API enforces one rule across all three routes, plus `PUT /users/{email}/reset-password`:
 * you may administer accounts **below your own tier**, never a peer and never a superior. It
 * is spelled out in {@link administrability}, which the UI uses to hide actions that would
 * only come back 403.
 */

/** The `detail` the API sends back on success. */
export interface LifecycleResponse {
  detail: string;
}

/**
 * Switch an account off. It takes the **email**, not the id: the only route in this area
 * that does, so do not hand it `user.id`.
 *
 * 403 `"You cannot deactivate your own account."` is a real answer: nobody, Superadmin
 * included, may lock themselves out. {@link administrability} refuses the caller's own row
 * for that reason.
 */
export const deactivateUser = (email: string) =>
  axios.put<LifecycleResponse>(
    `${AUTH_BASE_URL}users/${encodeURIComponent(email)}/deactivate`,
    undefined,
    { headers: authHeaders() }
  );

/** Switch it back on. Same tier rule, same email-not-id path. */
export const reactivateUser = (email: string) =>
  axios.put<LifecycleResponse>(
    `${AUTH_BASE_URL}users/${encodeURIComponent(email)}/reactivate`,
    undefined,
    { headers: authHeaders() }
  );

export type SecondFactor = 'totp';

/**
 * Clear one of somebody's second factors: this route takes the **user id**.
 *
 * Only ever a disable. `{enable: true}` answers 400 pointing at the self-service enrolment
 * routes, because enrolment has to prove the *user* holds the authenticator or can read the
 * address; an administrator asserting it proves nothing.
 *
 * `method` defaults to `totp` server-side and is sent explicitly here. Clearing a factor
 * that is already off answers 200.
 */
export const clearSecondFactor = (userId: string, method: SecondFactor) =>
  axios.put<{ detail: string }>(
    `${AUTH_BASE_URL}users/${encodeURIComponent(userId)}/handle-mfa`,
    { disable: true, method },
    { headers: authHeaders() }
  );

/** Email the person a link to choose a new password; the admin never sees or sets one. */
export const sendPasswordResetEmail = (email: string) =>
  axios.post<{ detail?: string }>(
    `${AUTH_BASE_URL}users/${encodeURIComponent(email)}/password-reset-email`,
    {},
    { headers: authHeaders() }
  );

/* ------------------------------------------------------------------ *
 * The tier rule
 * ------------------------------------------------------------------ */

/**
 * The ranking lives in `config/capabilities.ts`, because the capability grid greys out the
 * columns an operator may not configure on exactly this rule. Everything outside the two
 * named tiers ranks 0, every custom role a workspace defines included, which is deliberate:
 * the API decides, so an Admin is offered the action and the server has the final say.
 * Guessing the other way would hide a legitimate action with no way to discover it.
 */
const SUPERADMIN = ROLE_TIER_RANK.SUPERADMIN;

const rankOf = (roles: (string | null | undefined)[]): number =>
  roles.reduce<number>((highest, role) => Math.max(highest, roleTierRank(role)), 0);

export interface AdministrableTarget {
  email?: string;
  roles?: string[];
}

export interface Administrability {
  allowed: boolean;
  /** Why not. Shown next to the disabled control, never as a bare hidden state. */
  reason?: string;
}

const ALLOWED: Administrability = { allowed: true };

/**
 * Whether this caller may administer that account: deactivate or reactivate it, set its
 * password, or clear its second factor. All four follow the same rule.
 *
 * - A Superadmin may administer everyone, **including other Superadmins**.
 * - An Admin may administer the tiers below: Ciso, Analyst, Dev, Viewer, but not another
 *   Admin and not a Superadmin.
 * - Ciso and below may administer nobody, which is what `PLATFORM_USER`
 *   resolving to anything other than MANAGE already says.
 * - Nobody may administer their own account through these routes, Superadmins included:
 *   yourself is a peer, and self-service lives on the profile page.
 * - A workspace's own custom role holding MANAGE sits at no known tier, so the action is
 *   offered and the server's answer is what decides it.
 *
 * `capabilityHeld` comes from the workspace's own capability row rather than from the
 * roles on the login response, which arrives empty.
 */
export const administrability = ({
  capabilityHeld,
  callerRole,
  callerEmail,
  target
}: {
  capabilityHeld: boolean;
  callerRole?: string | null;
  callerEmail?: string | null;
  target: AdministrableTarget;
}): Administrability => {
  if (!capabilityHeld) {
    return {
      allowed: false,
      reason: 'Your role does not permit administering users in this workspace.'
    };
  }

  if (
    callerEmail &&
    target.email &&
    callerEmail.trim().toLowerCase() === target.email.trim().toLowerCase()
  ) {
    return {
      allowed: false,
      reason:
        'This is your own account. Change your password and second factor from your profile.'
    };
  }

  const caller = rankOf([callerRole]);
  if (caller >= SUPERADMIN) return ALLOWED;
  if (caller < 1) {
    // Holds the capability but is not one of the two named tiers: a workspace's own custom
    // role. Where that sits is the server's answer to give, so the action is offered and its
    // refusal surfaced, rather than hidden behind a guess that would leave the holder with a
    // control they can never find.
    return ALLOWED;
  }

  return rankOf(target.roles ?? []) < caller
    ? ALLOWED
    : { 
      allowed: false, 
      reason: 'You may only administer users below your own tier.' 
    };
};

/** Wording for the refusals these four routes actually answer with. */
export const LIFECYCLE_ERRORS: Record<number, string> = {
  403: 'You may only administer users below your own tier.',
  404: 'That account is not in your organisation.'
};

/**
 * Wording for `DELETE /workspace/users/{id}`, which refuses differently.
 *
 * Its 404 covers two cases deliberately and the server will not say which: they are not a
 * member of this workspace, or the workspace is outside your scope. Separating them would
 * let one customer probe another's workspace ids, so the copy has to cover both.
 */
export const MEMBERSHIP_ERRORS: Record<number, string> = {
  403: 'You may only remove members below your own tier, and never yourself.',
  404: 'That account is not a member of this workspace.'
};

export default {
  deactivateUser,
  reactivateUser,
  clearSecondFactor,
  sendPasswordResetEmail,
  administrability
};
